import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, RotateCcw, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MobileHeader } from "@/components/mobile/MobileHeader";
import { MobileMenu } from "@/components/mobile/MobileMenu";
import { ImageUploader } from "@/components/mobile/ImageUploader";
import { RoomRefreshInputs } from "@/components/RoomRefreshInputs";
import { BottomTabBar } from "@/components/mobile/BottomTabBar";
import { useGenerationSurveyGate } from "@/hooks/useGenerationSurveyGate";
import { GenerationSurveyDialog } from "@/components/GenerationSurveyDialog";
import { SubscriptionTier, getRetentionConfig } from "@/lib/retention";
import { calculateRemainingCredits } from "@/lib/tier-limits";
import { readCachedCredits, writeProfileSnapshot } from "@/lib/profile-snapshot";
import { uploadImageToStorage } from "@/lib/storage";
import { useLoginToastNotifications } from "@/hooks/useLoginToastNotifications";
import { EMPTY_ROOM_REFRESH_OPTIONS, RoomRefreshOptions } from "@/lib/room-refresh-options";

const downscaleImage = (file: File, maxDimension: number = 1024): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = (height / width) * maxDimension;
          width = maxDimension;
        } else {
          width = (width / height) * maxDimension;
          height = maxDimension;
        }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

const MAX_IMAGE_MB = 5;
const MAX_IMAGE_BYTES = MAX_IMAGE_MB * 1024 * 1024;

// Estimate decoded byte size of a base64 data URL (base64 length * 3/4, minus padding).
const dataUrlSizeBytes = (dataUrl: string): number => {
  const base64 = dataUrl.split(",")[1] ?? "";
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return (base64.length * 3) / 4 - padding;
};

const MobileRoomRefresh = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState<any>(null);
  useLoginToastNotifications(user?.id || null);
  const [cachedProfile] = useState(readCachedCredits);
  const [credits, setCredits] = useState(cachedProfile?.credits ?? 0);
  const [subscriptionTier, setSubscriptionTier] = useState<SubscriptionTier>(
    (cachedProfile?.tier as SubscriptionTier) ?? "free"
  );
  const [totalGenerations, setTotalGenerations] = useState(0);
  const [loadingUser, setLoadingUser] = useState(cachedProfile === null);

  const [unreadCount, setUnreadCount] = useState(0);

  const { loading: surveyLoading, needsSurvey, markCompleted } = useGenerationSurveyGate(
    user?.id,
    totalGenerations
  );
  const [surveyOpen, setSurveyOpen] = useState(false);
  const pendingGenerationRef = useRef(false);

  const [menuOpen, setMenuOpen] = useState(false);

  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [prompt, setPrompt] = useState("");
  const [references, setReferences] = useState<string[]>([]);
  const [refreshOptions, setRefreshOptions] = useState<RoomRefreshOptions>(EMPTY_ROOM_REFRESH_OPTIONS);
  const [generating, setGenerating] = useState(false);

  // Load user + profile (mirrors MobileDesigner, minus room/style logic).
  const loadProfile = useCallback(async (userId: string) => {
    const { data: profile } = await supabase
      .from("profiles")
      .select("subscription_tier,designs_this_month,total_generations,bonus_generations")
      .eq("id", userId)
      .single();
    if (profile) {
      setSubscriptionTier(profile.subscription_tier as SubscriptionTier);
      setTotalGenerations(profile.total_generations ?? 0);
      setCredits(
        calculateRemainingCredits(
          profile.subscription_tier,
          profile.designs_this_month,
          profile.bonus_generations
        )
      );
      writeProfileSnapshot(userId, {
        subscription_tier: profile.subscription_tier,
        bonus_generations: profile.bonus_generations ?? 0,
        designs_this_month: profile.designs_this_month ?? 0,
        total_generations: profile.total_generations ?? 0,
      });
    }

  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          if (navigator.onLine) navigate("/auth");
          return;
        }
        const currentUser = session.user;
        setUser(currentUser);
        if (navigator.onLine) {
          await loadProfile(currentUser.id);
          const { count } = await supabase
            .from("user_notifications")
            .select("*", { count: "exact", head: true })
            .eq("user_id", currentUser.id)
            .eq("is_read", false);
          setUnreadCount(count || 0);
        }
      } catch (error) {
        console.error("Error loading user:", error);
      } finally {
        setLoadingUser(false);
      }
    };
    loadUser();
  }, [navigate, loadProfile]);

  const handleImageUpload = useCallback(async (file: File) => {
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(`Image must be less than ${MAX_IMAGE_MB}MB`);
      return;
    }
    setUploadingImage(true);
    try {
      const dataUrl = await downscaleImage(file);
      setUploadedImage(dataUrl);
      toast.success("Image uploaded successfully");
      uploadImageToStorage(dataUrl, "original").then((storageUrl) => {
        if (storageUrl) setUploadedImage(storageUrl);
      });
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error("Failed to process image");
    } finally {
      setUploadingImage(false);
    }
  }, []);

  const handleImageDataUrl = useCallback(async (dataUrl: string) => {
    if (dataUrlSizeBytes(dataUrl) > MAX_IMAGE_BYTES) {
      toast.error(`Image must be less than ${MAX_IMAGE_MB}MB`);
      return;
    }
    setUploadingImage(true);
    try {
      setUploadedImage(dataUrl);
      toast.success("Photo selected");
      uploadImageToStorage(dataUrl, "original").then((storageUrl) => {
        if (storageUrl) setUploadedImage(storageUrl);
      });
    } catch (error) {
      console.error("Error processing image:", error);
      toast.error("Failed to process image");
    } finally {
      setUploadingImage(false);
    }
  }, []);

  const handleRemoveImage = useCallback(() => {
    setUploadedImage(null);
  }, []);

  const handleStartOver = useCallback(() => {
    setUploadedImage(null);
    setPrompt("");
    setReferences([]);
    setRefreshOptions(EMPTY_ROOM_REFRESH_OPTIONS);
    toast.success("Started a new refresh");
  }, []);

  const canGenerate = uploadedImage && credits >= 1 && !generating;

  const handleGenerate = async () => {
    if (generating) return;
    if (!user) {
      navigate("/auth");
      return;
    }
    if (!uploadedImage) {
      toast.error("Upload a photo of your room to get started");
      return;
    }

    // Mandatory survey gate (same wiring as the standard designer).
    if (needsSurvey && !pendingGenerationRef.current) {
      setSurveyOpen(true);
      return;
    }
    pendingGenerationRef.current = false;

    if (credits < 1) {
      toast.error("Not enough credits. Upgrade for more!");
      return;
    }

    setGenerating(true);

    // Upload reference images to storage so the edge function can fetch them.
    let refUrls: string[] = [];
    if (references.length > 0) {
      const uploaded = await Promise.all(
        references.map((r) => uploadImageToStorage(r, "reference"))
      );
      refUrls = uploaded.filter((u): u is string => Boolean(u));
    }

    // Navigate to the shared Results page, which runs generation and auto-saves.
    // designStyle/roomType are set to "Room Refresh" so the saved_designs row
    // satisfies the NOT NULL constraints and reads cleanly in Projects/Explore.
    navigate("/results", {
      state: {
        originalImage: uploadedImage,
        transformedImages: [],
        roomType: "Room Refresh",
        designStyle: "Room Refresh",
        isGenerating: true,
        customIdeas: prompt || undefined,
        totalVariations: 1,
        userId: user.id,
        subscriptionTier,
        // Room-Refresh-specific generation params.
        mode: "room_refresh",
        referenceImages: refUrls,
        refreshOptions,
      },
    });
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#FFF7F1] pb-48">
      <MobileHeader
        credits={credits}
        loading={loadingUser}
        onMenuClick={() => setMenuOpen(true)}
        onNotificationClick={() => navigate("/notifications")}
        onExploreClick={() => navigate("/explore")}
        onBlogClick={() => navigate("/blog")}
        unreadCount={unreadCount}
      />
      <MobileMenu
        open={menuOpen}
        onOpenChange={setMenuOpen}
        subscriptionTier={subscriptionTier}
        onSignOut={handleSignOut}
      />

      <div className="px-4 pt-safe-header space-y-5">
        {/* Intro */}
        <section className="rounded-2xl bg-white border border-border p-4">
          <div className="flex items-center gap-2 mb-1.5">
            <Wand2 className="w-4 h-4 text-primary" />
            <h2 className="text-base font-semibold">Refresh your room</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            Give your room a fresh look without starting over. Room Refresh keeps your existing furniture and layout while making the space feel newer, cleaner and more put together.
          </p>
        </section>

        {/* Upload */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-xs text-muted-foreground">
              Upload a photo <span className="text-destructive">*</span>
            </Label>
            {uploadedImage && (
              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={handleStartOver}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
              </Button>
            )}
          </div>
          <ImageUploader
            image={uploadedImage}
            loading={uploadingImage}
            onUpload={handleImageUpload}
            onImageDataUrl={handleImageDataUrl}
            onRemove={handleRemoveImage}
          />
        </section>

        {/* Prompt + references */}
        {uploadedImage && (
          <section className="rounded-2xl bg-white border border-border p-4">
            <RoomRefreshInputs
              prompt={prompt}
              onPromptChange={setPrompt}
              references={references}
              onReferencesChange={setReferences}
              options={refreshOptions}
              onOptionsChange={setRefreshOptions}
              compact
            />
          </section>
        )}
      </div>

      {/* Fixed bottom dock: action button stacked above the tab bar */}
      <div className="fixed bottom-0 inset-x-0 z-40">
        <div className="bg-[#FFF7F1] border-t border-border p-2">
          <Button
            onClick={handleGenerate}
            className={`w-full h-12 text-base font-semibold rounded-full ${
              canGenerate ? "bg-[#7AB36B] hover:bg-green-600" : "bg-[#7AB36B]/50 cursor-not-allowed"
            }`}
          >
            <Sparkles className="h-5 w-5" />
            {generating ? "Refreshing..." : "Refresh My Room"}
          </Button>
          {!loadingUser && credits < 1 && (
            <p className="text-xs text-destructive text-center mt-2">
              Not enough credits. You have {credits} credits.
            </p>
          )}
        </div>
        <BottomTabBar />
      </div>


      <GenerationSurveyDialog
        open={surveyOpen}
        onOpenChange={setSurveyOpen}
        userId={user?.id ?? ""}
        userEmail={user?.email}
        userName={user?.user_metadata?.full_name}
        onSubmitted={() => {
          markCompleted();
          pendingGenerationRef.current = true;
          setSurveyOpen(false);
          handleGenerate();
        }}
      />
    </div>

  );
};

export default MobileRoomRefresh;
